import { Injectable } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import * as models from '../models';
import { HelperService } from './helper.service';

export class ZoneRevisionConflictError extends Error {
    constructor(
        public resource: 'zones' | 'floor-plan',
        public resourceId: string,
        public expectedRevision: number,
        public actualRevision: number,
    ) {
        super(`${resource} changed from revision ${expectedRevision} to ${actualRevision}`);
        this.name = 'ZoneRevisionConflictError';
    }
}

export interface FloorPlanWorkspaceChange {
    floorPlan: models.FloorPlan;
    expectedRevision: number;
}

@Injectable()
export class ZoneService {

    cache: { [key: string]: Array<models.Zone> } = {};
    dbZoneCache: { [key: string]: models.DbZone } = {};

    companyRef: AngularFirestoreCollection;
    collectionId: string = 'grow-settings';
    itemId: string = 'zones';

    constructor(
        private firestore: AngularFirestore,
        private helperService: HelperService,
    ) {
        this.companyRef = this.firestore.collection('companies');
    }

    async getZonesObject(): Promise<models.DbZone> {
        let companyId = this.helperService.currentCompanyId;
        let response = this.companyRef.doc(companyId).collection(this.collectionId).doc(this.itemId);
        let dbZone: models.DbZone = await (await response.get().toPromise()).data();

        return dbZone;
    }

    async getDbZone(): Promise<models.DbZone> {
        let companyId = this.helperService.currentCompanyId;

        //get from memory cache
        if(this.dbZoneCache[companyId] != null){
            return this.dbZoneCache[companyId];
        }

        let response = this.companyRef.doc(companyId).collection(this.collectionId).doc(this.itemId);
        let dbZone: models.DbZone = await (await response.get().toPromise()).data();

        //save to memory cache
        if(dbZone != null){
            this.dbZoneCache[companyId] = dbZone;
        }

        return dbZone;
    }

    async getAll(): Promise<Array<models.Zone>> {
        let companyId = this.helperService.currentCompanyId;

        //get from memory cache
        if(this.cache[companyId] != null){
            return this.cache[companyId];
        }

        let response = this.companyRef.doc(companyId).collection(this.collectionId).doc(this.itemId);
        let dbZone: models.DbZone = await (await response.get().toPromise()).data();

        //save to memory cache
        let list = dbZone?.list || [];
        if(list.length > 0){
            this.cache[companyId] = list;
        }

        return list;
    }

    async update(zoneObject: models.DbZone): Promise<void> {
        // Legacy callers now participate in the same optimistic-concurrency
        // contract instead of bypassing the revision maintained by Dutchie and
        // the unified workspace.
        let result = await this.saveWorkspace(zoneObject, this.validRevision(zoneObject?.revision));
        zoneObject.revision = result.zoneRevision;
    }

    // Saves the complete zone working copy and dirty plan drawings atomically.
    // All reads happen before writes so Firestore can safely retry the transaction.
    async saveWorkspace(
        zoneObject: models.DbZone,
        expectedZoneRevision: number,
        floorPlanChanges: FloorPlanWorkspaceChange[] = [],
    ): Promise<{ zoneRevision: number, floorPlanRevisions: { [id: string]: number } }> {
        let companyId = this.helperService.currentCompanyId;
        let zoneRef = this.firestore.collection('companies').doc(companyId)
            .collection(this.collectionId).doc(this.itemId).ref;
        let planRefs = floorPlanChanges.map(change => ({
            change,
            ref: this.firestore.collection('companies').doc(companyId)
                .collection('floor-plans').doc(change.floorPlan.id).ref,
        }));

        let result = await this.firestore.firestore.runTransaction(async transaction => {
            let zoneSnapshot = await transaction.get(zoneRef);
            let currentZone: models.DbZone = (zoneSnapshot.data() as models.DbZone) || {};
            let actualZoneRevision = this.validRevision(currentZone.revision);
            let expected = this.validRevision(expectedZoneRevision);
            if (actualZoneRevision !== expected) {
                throw new ZoneRevisionConflictError('zones', this.itemId, expected, actualZoneRevision);
            }

            // Firestore transactions require every read before the first write.
            let planSnapshots = [];
            for (const item of planRefs) {
                planSnapshots.push({ item, snapshot: await transaction.get(item.ref) });
            }

            planSnapshots.forEach(({ item, snapshot }) => {
                let current = (snapshot.data() as models.FloorPlan) || {};
                let actual = this.validRevision(current.revision);
                let planExpected = this.validRevision(item.change.expectedRevision);
                if (actual !== planExpected) {
                    throw new ZoneRevisionConflictError('floor-plan', item.change.floorPlan.id, planExpected, actual);
                }
            });

            let nextZoneRevision = actualZoneRevision + 1;
            let zoneWrite = this.firestoreValue({ ...zoneObject, revision: nextZoneRevision });
            transaction.set(zoneRef, zoneWrite);

            let floorPlanRevisions: { [id: string]: number } = {};
            planSnapshots.forEach(({ item }) => {
                let nextRevision = this.validRevision(item.change.expectedRevision) + 1;
                let { id, file, ...persisted } = item.change.floorPlan;
                transaction.set(item.ref, this.firestoreValue({ ...persisted, revision: nextRevision }));
                floorPlanRevisions[id] = nextRevision;
            });

            return { zoneRevision: nextZoneRevision, floorPlanRevisions };
        });

        this.cache[companyId] = null;
        this.dbZoneCache[companyId] = null;
        return result;
    }

    async deleteFloorPlan(
        zoneObject: models.DbZone,
        expectedZoneRevision: number,
        floorPlanId: string,
        expectedFloorPlanRevision: number,
    ): Promise<number> {
        let companyId = this.helperService.currentCompanyId;
        let zoneRef = this.firestore.collection('companies').doc(companyId)
            .collection(this.collectionId).doc(this.itemId).ref;
        let planRef = this.firestore.collection('companies').doc(companyId)
            .collection('floor-plans').doc(floorPlanId).ref;

        let revision = await this.firestore.firestore.runTransaction(async transaction => {
            let zoneSnapshot = await transaction.get(zoneRef);
            let planSnapshot = await transaction.get(planRef);
            let actualZoneRevision = this.validRevision((zoneSnapshot.data() as models.DbZone)?.revision);
            let actualPlanRevision = this.validRevision((planSnapshot.data() as models.FloorPlan)?.revision);
            if (actualZoneRevision !== this.validRevision(expectedZoneRevision)) {
                throw new ZoneRevisionConflictError('zones', this.itemId, expectedZoneRevision, actualZoneRevision);
            }
            if (planSnapshot.exists && actualPlanRevision !== this.validRevision(expectedFloorPlanRevision)) {
                throw new ZoneRevisionConflictError('floor-plan', floorPlanId, expectedFloorPlanRevision, actualPlanRevision);
            }
            let nextRevision = actualZoneRevision + 1;
            transaction.set(zoneRef, this.firestoreValue({ ...zoneObject, revision: nextRevision }));
            transaction.delete(planRef);
            return nextRevision;
        });

        this.cache[companyId] = null;
        this.dbZoneCache[companyId] = null;
        return revision;
    }

    private validRevision(value: number): number {
        return Number.isFinite(value) && value >= 0 ? value : 0;
    }

    private firestoreValue<T>(value: T): T {
        // Existing models contain optional undefined properties; Firestore rejects
        // them, while JSON normalization preserves the document's intended shape.
        return JSON.parse(JSON.stringify(value));
    }

    //non-db calls
    getListOfParents(zones: Array<models.Zone>, id: string) {
        let parentIds: Array<string> = [];
        let parent: models.Zone = this.getParent({ zones: zones }, id);

        if (parent != null && parent.id != null) {
            id = parent.id;
            parentIds.unshift(id);
        }

        while (parent != null) {
            parent = this.getParent({ zones: zones }, id);
            if (parent != null) {
                id = parent.id;
                if (id != null) {
                    parentIds.unshift(id);
                }
            }
        }

        return parentIds;
    }

    getListOfParentNames(zones: Array<models.Zone>, id: string) {
        let parentNames: Array<string> = [];
        let parent: models.Zone = this.getParent({ zones: zones }, id);

        if (parent != null && parent.name != null) {
            id = parent.id;
            parentNames.unshift(parent.name);
        }

        while (parent != null) {
            parent = this.getParent({ zones: zones }, id);
            if (parent != null) {
                id = parent.id;
                if (id != null) {
                    parentNames.unshift(parent.name);
                }
            }
        }

        return parentNames;
    }

    getParent(zone: models.Zone, id: string): models.Zone {
      var result;
      (zone.zones || []).some(o => result = o.id === id ? zone : this.getParent(o, id));
      return result;
    }

    getZone(zones: Array<models.Zone>, id: string): models.Zone {
        let zone;

        zone = zones.find(i => i.id == id);

        if(zone != null){
            return zone;
        }

        for (let i = 0; i < zones.length; i++) {
            const z = zones[i];
            zone = this.getZone(z.zones, id);
            if(zone != null){
                return zone;
            }
        }
    }

    getListOfChildIds(zones: Array< models.Zone>, id: string): Array<string> {
        let retVal: Array<string> = [];
        let zone = this.getZone(zones, id);
        if(zone != null && zone.zones != null){
            retVal.push(... this.returnListOfChildIds(zone.zones));
        }
        return retVal;
    }

    returnListOfChildIds(zones: Array<models.Zone>) {
        let retVal: Array<string> = [];

        zones.forEach(zone => {
            retVal.push(zone.id);
            if(zone.zones != null){
                let childIds = this.returnListOfChildIds(zone.zones);
                retVal.push(... childIds);
            }
        });

        return retVal;
    }

    getZoneHeirachryString(zones: Array<models.Zone>, zoneId: string) {
      let zone = this.getZone(zones, zoneId);
      let parents = this.getListOfParentNames(zones, zoneId);

      let zoneHierarchyString: string = zone.name;

      for (let index = parents.length-1; index >= 0; index--) {
        const element = parents[index];
        
        zoneHierarchyString = `${element} -> ${zoneHierarchyString}`
      }

      return zoneHierarchyString;
    }

    getAllTopLevel(zones: Array<models.Zone>, zoneIds: string[]): models.Zone[] {
        if(zones == null){
            return [];
        }
        let allIds = []
        zoneIds.forEach(zoneId => {
            allIds.push(zoneId);
            let parentIds = this.getListOfParents(zones, zoneId)
            allIds.push(...parentIds);
        });

        return zones.filter(i => allIds.includes(i.id));
    }
}
